import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, IconButton, Button } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { tokens } from '../../theme/tokens';

// ---------------------------------------------------------------------------
// Toast severities + the single sanctioned place for raw type hex.
//
// No central semantic-palette module exists in this codebase (the type colours
// are repeated inline across the app and in App.tsx's MUI palette). The approved
// design colours below match that palette exactly: success.dark #15803D,
// error.main #dc2626, warning.dark #b45309, info uses tokens.ink (calmer on a
// white card). Every other colour on the card comes from design tokens.
// ---------------------------------------------------------------------------
export type ToastSeverity = 'success' | 'error' | 'info' | 'warning';

type ToastTypeMeta = {
  color: string;
  Icon: React.ElementType;
  assertive: boolean; // error/warning -> role="alert" + aria-live="assertive"
};

const TOAST_TYPE: Record<ToastSeverity, ToastTypeMeta> = {
  success: { color: '#15803D', Icon: CheckCircleIcon, assertive: false },
  error: { color: '#dc2626', Icon: ErrorOutlineIcon, assertive: true },
  warning: { color: '#b45309', Icon: WarningAmberIcon, assertive: true },
  info: { color: tokens.ink, Icon: InfoOutlinedIcon, assertive: false },
};

const DEFAULT_DURATION = 4000;
const ERROR_DURATION = 6000;

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastOptions {
  severity?: ToastSeverity;
  duration?: number;
  action?: ToastAction;
}

interface ToastState {
  id: number;
  message: string;
  severity: ToastSeverity;
  duration: number;
  action?: ToastAction;
}

// The callable hook return: `toast(message, opts?)` plus convenience methods.
export interface ToastApi {
  (message: string, opts?: ToastOptions): void;
  success: (message: string, opts?: Omit<ToastOptions, 'severity'>) => void;
  error: (message: string, opts?: Omit<ToastOptions, 'severity'>) => void;
  info: (message: string, opts?: Omit<ToastOptions, 'severity'>) => void;
  warning: (message: string, opts?: Omit<ToastOptions, 'severity'>) => void;
}

type ToastPosition = 'bottom-center' | 'bottom-right' | 'bottom-left' | 'top-center';

const ToastContext = createContext<ToastApi | null>(null);

// ---------------------------------------------------------------------------
// Presentational card — compact branded surface. Kept dumb: the provider owns
// state/timers and passes everything in.
// ---------------------------------------------------------------------------
interface ToastCardProps {
  toast: ToastState;
  onDismiss: () => void;
}

const ToastCard: React.FC<ToastCardProps> = ({ toast, onDismiss }) => {
  const reduceMotion = useReducedMotion();
  const meta = TOAST_TYPE[toast.severity];
  const { Icon } = meta;

  return (
    <motion.div
      initial={reduceMotion ? false : { opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 16 }}
      transition={{ duration: 0.26, ease: [0.16, 1, 0.3, 1] }}
      style={{ pointerEvents: 'auto' }}
    >
      <Box
        role={meta.assertive ? 'alert' : 'status'}
        aria-live={meta.assertive ? 'assertive' : 'polite'}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          minWidth: 288,
          maxWidth: 440,
          pl: 1.75,
          pr: 1,
          py: 1.25,
          position: 'relative',
          overflow: 'hidden',
          bgcolor: tokens.surface,
          border: `1px solid ${tokens.border}`,
          borderRadius: '12px',
          boxShadow: tokens.softShadow,
          borderLeft: `4px solid ${meta.color}`,
        }}
      >
        <Icon sx={{ color: meta.color, fontSize: 20, flexShrink: 0 }} />
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            color: tokens.ink,
            fontSize: '0.875rem',
            fontWeight: 600,
            lineHeight: 1.4,
            wordBreak: 'break-word',
          }}
        >
          {toast.message}
        </Box>
        {toast.action && (
          <Button
            size="small"
            onClick={() => {
              toast.action?.onClick();
              onDismiss();
            }}
            sx={{
              flexShrink: 0,
              minHeight: 'auto',
              minWidth: 'auto',
              px: 1,
              py: 0.25,
              color: meta.color,
              fontWeight: 700,
              fontSize: '0.8125rem',
              '&:hover': { transform: 'none', boxShadow: 'none', bgcolor: 'rgba(26,20,15,0.04)' },
            }}
          >
            {toast.action.label}
          </Button>
        )}
        <IconButton
          size="small"
          aria-label="Dismiss notification"
          onClick={onDismiss}
          sx={{ flexShrink: 0, color: tokens.muted, '&:hover': { color: tokens.ink } }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
    </motion.div>
  );
};

// ---------------------------------------------------------------------------
// Positioning for the single host. bottom-center by default.
// ---------------------------------------------------------------------------
const POSITION_SX: Record<ToastPosition, object> = {
  'bottom-center': { bottom: 24, left: '50%', transform: 'translateX(-50%)', alignItems: 'center' },
  'bottom-right': { bottom: 24, right: 24, alignItems: 'flex-end' },
  'bottom-left': { bottom: 24, left: 24, alignItems: 'flex-start' },
  'top-center': { top: 24, left: '50%', transform: 'translateX(-50%)', alignItems: 'center' },
};

export interface ToastProviderProps {
  children: React.ReactNode;
  position?: ToastPosition;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({
  children,
  position = 'bottom-center',
}) => {
  const [toast, setToast] = useState<ToastState | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idRef = useRef(0);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const dismiss = useCallback(() => {
    clearTimer();
    setToast(null);
  }, [clearTimer]);

  const show = useCallback(
    (message: string, opts?: ToastOptions) => {
      const severity = opts?.severity ?? 'info';
      const duration =
        opts?.duration ?? (severity === 'error' ? ERROR_DURATION : DEFAULT_DURATION);
      const id = ++idRef.current;
      clearTimer();
      // Latest-wins single host — a new toast replaces the current one.
      setToast({ id, message, severity, duration, action: opts?.action });
      if (duration > 0) {
        timerRef.current = setTimeout(() => {
          // Only dismiss if this toast is still the one showing.
          setToast((current) => (current && current.id === id ? null : current));
          timerRef.current = null;
        }, duration);
      }
    },
    [clearTimer],
  );

  useEffect(() => clearTimer, [clearTimer]);

  const api = useMemo<ToastApi>(() => {
    const fn = ((message: string, opts?: ToastOptions) => show(message, opts)) as ToastApi;
    fn.success = (message, opts) => show(message, { ...opts, severity: 'success' });
    fn.error = (message, opts) => show(message, { ...opts, severity: 'error' });
    fn.info = (message, opts) => show(message, { ...opts, severity: 'info' });
    fn.warning = (message, opts) => show(message, { ...opts, severity: 'warning' });
    return fn;
  }, [show]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <Box
        sx={{
          position: 'fixed',
          zIndex: (t) => t.zIndex.snackbar,
          display: 'flex',
          flexDirection: 'column',
          pointerEvents: 'none',
          ...POSITION_SX[position],
        }}
      >
        <AnimatePresence>
          {toast && <ToastCard key={toast.id} toast={toast} onDismiss={dismiss} />}
        </AnimatePresence>
      </Box>
    </ToastContext.Provider>
  );
};

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a <ToastProvider>');
  }
  return ctx;
}
